import React, { useMemo } from "react";
import { Button } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { getPetitionStatusMeta, formatPetitionDate } from "utils/petitionHelpers";

const PetitionTable = ({
  rows,
  loading,
  reportingYear,
  regionFilter,
  onRegionFilterChange,
  statusFilter,
  onStatusFilterChange,
  search,
  onSearchChange,
  regionOptions,
  statusOptions,
  canReview,
  onReviewRow,
}) => {
  const sortedRows = useMemo(() => rows, [rows]);

  let lastRegion = null;

  return (
    <section className="rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-4 md:px-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground md:text-xl">
              District petitions by region
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {reportingYear} — {rows.length} district(s) shown, sorted by region then status
            </p>
          </div>
          <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:max-w-3xl lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Region
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={regionFilter}
                onChange={(e) => onRegionFilterChange(e.target.value)}
              >
                {regionOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Petition status
              </label>
              <select
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Search
              </label>
              <input
                type="search"
                placeholder="District or region…"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-left text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-semibold md:px-6">Region</th>
              <th className="px-4 py-3 font-semibold md:px-6">District</th>
              <th className="px-4 py-3 font-semibold md:px-6">Assessment</th>
              <th className="px-4 py-3 font-semibold md:px-6">Petition status</th>
              <th className="px-4 py-3 font-semibold md:px-6">Submitted</th>
              <th className="px-4 py-3 font-semibold md:px-6">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground md:px-6">
                  Loading district petitions…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground md:px-6">
                  No districts match the current filters.
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const meta = getPetitionStatusMeta(row.petitionStatus);
                const showRegionHeader = row.regionName !== lastRegion;
                lastRegion = row.regionName;

                return (
                  <React.Fragment key={row.districtId}>
                    {showRegionHeader && (
                      <tr className="bg-[#16325a]/5">
                        <td
                          colSpan={6}
                          className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#16325a] md:px-6"
                        >
                          {row.regionName}
                        </td>
                      </tr>
                    )}
                    <tr className="hover:bg-muted/30">
                      <td className="px-4 py-3 text-muted-foreground md:px-6">{row.regionName}</td>
                      <td className="px-4 py-3 font-medium text-foreground md:px-6">{row.districtName}</td>
                      <td className="px-4 py-3 text-muted-foreground md:px-6">
                        {row.assessmentStatus || "—"}
                      </td>
                      <td className="px-4 py-3 md:px-6">
                        <span
                          className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.badgeClass}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground md:px-6">
                        {row.hasPetitionText
                          ? formatPetitionDate(row.petitionSubmittedAt)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 md:px-6">
                        {row.hasPetitionText ? (
                          <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            onClick={() => onReviewRow(row)}
                            className="px-0"
                          >
                            {canReview && row.petitionStatus === "pending" ? "Review" : "View"}
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default PetitionTable;
